import * as THREE from "three";
import { mergeVertices } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { entityDefinitions } from "../../data/entities";
import { FACE, G, Kit, hdr, instance, once, profileSlab } from "./kit";
import { blobShadow, decalMaterial, printMaterial } from "./fx";
import { DISPLAY_FONT, canvasTex, stripesTex } from "./tex";

/** Street furniture in the lanes: dumpster, power box, traffic-light pole, hedge, pillar. Origin = footprint centre on the road, front = −z. */

const DUMP = entityDefinitions.dumpster;     // 3.0 × 2.0 × 1.6
const POWER = entityDefinitions.powerBox;    // 2.0 × 1.6 × 2.4
const POLE = entityDefinitions.pole;         // 1.2 × 1.2 × 7.28
const PILLAR = entityDefinitions.pillar;     // 1.2 × 1.2 × 6

const stencil = (key: string, text: string, color = "#ffffff"): THREE.Texture => canvasTex(`stencil:${key}`, 512, 128, (ctx, w, h) => {
  ctx.fillStyle = color;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `900 92px ${DISPLAY_FONT}`;
  ctx.fillText(text, w / 2, h / 2 + 4);
  // stencil bridges
  ctx.globalCompositeOperation = "destination-out";
  ctx.fillRect(0, h / 2 - 3, w, 6);
});

/** `buildDumpster()` → a green front-load dumpster with black lids, fork pockets, casters, "GAS FEES" stencil. */
export function buildDumpster(): THREE.Group {
  return instance("dumpster", () => {
    const kit = new Kit();
    const w = DUMP.width, hz = DUMP.depth / 2, top = DUMP.height;
    const green = 0x23955a;
    kit.add(profileSlab([[-hz + 0.15, 0.16], [hz - 0.12, 0.16], [hz, top - 0.16], [-hz, top - 0.16]], w - 0.1, 0.07, { bevelSegments: 2 }), "satin", green);
    kit.add(G.chamfer(w, 0.1, DUMP.depth + 0.06, 0.03), "satin", 0x1b7144, { p: [0, top - 0.13, 0] });
    for (const x of [-1, 1]) {
      kit.add(G.chamfer(w / 2 - 0.04, 0.09, DUMP.depth + 0.08, 0.03), "plastic", 0x1d1e24, { p: [x * (w / 4), top - 0.04, 0.0], r: [0, 0, -x * 0.03] });
      kit.add(G.box(0.12, 0.3, DUMP.depth - 0.5), "satin", 0x1b7144, { p: [x * (w / 2 - 0.02), 0.9, 0] });
      for (const z of [-hz + 0.3, hz - 0.3]) {
        kit.add(G.box(0.16, 0.08, 0.16), "satin", 0x2b2d36, { p: [x * (w / 2 - 0.3), 0.16, z] });
        kit.add(G.cyl(0.08, 0.08, 0.07, 10), "rubber", 0x1d1e24, { p: [x * (w / 2 - 0.3), 0.08, z], r: [0, 0, Math.PI / 2] });
      }
    }
    // a horizontal rib and the stencil on the front, a stripe of reflector tape
    kit.add(G.box(w - 0.2, 0.08, 0.05), "satin", 0x1b7144, { p: [0, 0.42, -hz + 0.13], r: [-0.1, 0, 0] });
    kit.add(G.plane(2.2, 0.55), decalMaterial("dumpster-stencil", stencil("dumpster", "GAS FEES"), { roughness: 0.6 }), 0xffffff, { p: [0, 0.95, -hz + 0.048], r: [-0.1, Math.PI, 0] });
    for (const x of [-1, 1]) kit.add(G.box(0.3, 0.1, 0.02), "glow", hdr(0xff3348, 1.1), { p: [x * (w / 2 - 0.35), 0.32, -hz + 0.13] });
    const root = new THREE.Group();
    root.name = "dumpster";
    root.add(kit.build(), blobShadow(w + 1, DUMP.depth + 1.2));
    return root;
  });
}

const voltageTex = (): THREE.Texture => canvasTex("sign-voltage", 256, 256, (ctx, w, h) => {
  ctx.beginPath();
  ctx.moveTo(w / 2, 18); ctx.lineTo(w - 14, h - 26); ctx.lineTo(14, h - 26); ctx.closePath();
  ctx.fillStyle = "#17181f"; ctx.fill();
  ctx.beginPath();
  ctx.moveTo(w / 2, 44); ctx.lineTo(w - 38, h - 40); ctx.lineTo(38, h - 40); ctx.closePath();
  ctx.fillStyle = "#ffc21a"; ctx.fill();
  ctx.beginPath();
  ctx.moveTo(w / 2 + 14, 92); ctx.lineTo(w / 2 - 28, 160); ctx.lineTo(w / 2 + 2, 160); ctx.lineTo(w / 2 - 14, 212); ctx.lineTo(w / 2 + 30, 140); ctx.lineTo(w / 2 + 2, 140);
  ctx.closePath();
  ctx.fillStyle = "#17181f"; ctx.fill();
});

/** `buildPowerBox()` → a street electrical cabinet on a plinth: two doors, vents, a high-voltage sign. */
export function buildPowerBox(): THREE.Group {
  return instance("powerBox", () => {
    const kit = new Kit();
    const w = POWER.width, d = POWER.depth, h = POWER.height;
    const body = 0x8fa39a;
    kit.add(G.chamfer(w, 0.22, d, 0.04), "matte", 0xbab5ad, { p: [0, 0.11, 0] });
    kit.add(G.chamfer(w - 0.16, h - 0.36, d - 0.2, 0.05), "satin", body, { p: [0, 0.22 + (h - 0.36) / 2, 0] });
    kit.add(G.chamfer(w + 0.04, 0.1, d + 0.02, 0.04), "satin", 0x6f817a, { p: [0, h - 0.09, 0] });
    for (const x of [-1, 1]) {
      kit.add(G.chamfer((w - 0.3) / 2, h - 0.6, 0.05, 0.02), "satin", 0x97aba2, { p: [x * (w - 0.3) / 4 + x * 0.02, 0.22 + (h - 0.36) / 2, -(d - 0.2) / 2 - 0.02] });
      kit.add(G.box(0.05, 0.22, 0.06), "chrome", 0xdfe3ea, { p: [x * 0.12, 1.25, -(d - 0.2) / 2 - 0.06] });
      for (let i = 0; i < 4; i++) kit.add(G.box((w - 0.5) / 2 - 0.1, 0.035, 0.04), "satin", 0x5e6f68, { p: [x * (w - 0.3) / 4 + x * 0.02, 0.5 + i * 0.09, -(d - 0.2) / 2 - 0.05] });
      kit.add(G.cyl(0.07, 0.07, h - 0.3, 10), "satin", 0x7a7f8c, { p: [x * (w / 2 - 0.02), (h - 0.3) / 2 + 0.2, d / 2 - 0.35] });
    }
    kit.add(G.plane(0.5, 0.5), decalMaterial("sign-voltage", voltageTex(), { roughness: 0.4 }), 0xffffff, { p: [-(w - 0.3) / 4 - 0.02, 1.7, -(d - 0.2) / 2 - 0.05], r: FACE.front });
    kit.add(G.box(0.12, 0.12, 0.04), "glow", hdr(0x14f195, 1.5), { p: [(w - 0.3) / 4 + 0.02, 1.85, -(d - 0.2) / 2 - 0.05] });
    const root = new THREE.Group();
    root.name = "powerBox";
    root.add(kit.build(), blobShadow(w + 1, d + 1));
    return root;
  });
}

/** `buildTrafficPole()` → a traffic-light pole (7.28 m) between lanes: hazard sleeve, signal head facing −z, red lit. */
export function buildTrafficPole(): THREE.Group {
  return instance("trafficPole", () => {
    const kit = new Kit();
    const top = POLE.height;
    kit.add(G.cyl(0.34, 0.38, 0.24, 18), "matte", 0xbab5ad, { p: [0, 0.12, 0] });
    kit.add(G.cyl(0.13, 0.15, top - 1.4, 16), "satin", 0x3b404d, { p: [0, 0.24 + (top - 1.4) / 2, 0] });
    kit.add(G.cyl(0.16, 0.16, 1.1, 16), printMaterial("pole-sleeve", stripesTex("#ffc21a", "#17181f", 4, 256, 128), { roughness: 0.45 }), 0xffffff, { p: [0, 0.85, 0] });
    // signal head: backplate, housing, three lenses with visors
    const headY = top - 0.78;
    kit.add(G.chamfer(0.9, 1.62, 0.05, 0.03), "plastic", 0xffc21a, { p: [0, headY, 0.12] });
    kit.add(G.chamfer(0.8, 1.52, 0.06, 0.03), "plastic", 0x17181f, { p: [0, headY, 0.09] });
    kit.add(G.chamfer(0.5, 1.42, 0.38, 0.06), "plastic", 0x22242c, { p: [0, headY, -0.1] });
    const visor = once("g:visor", () => new THREE.CylinderGeometry(0.2, 0.2, 0.28, 14, 1, true, -Math.PI / 2, Math.PI));
    const lights: [number, number, boolean][] = [[0.45, 0xff2a2a, true], [0, 0xffb11a, false], [-0.45, 0x22e07a, false]];
    for (const [dy, c, on] of lights) {
      kit.add(G.circle(0.155, 18), on ? "glow" : "glass", on ? hdr(c, 2.0) : new THREE.Color(c).multiplyScalar(0.18), { p: [0, headY + dy, -0.293], r: FACE.front });
      kit.add(visor, "plastic", 0x17181f, { p: [0, headY + dy + 0.02, -0.42], r: [Math.PI / 2, 0, 0] });
    }
    kit.add(G.chamfer(0.34, 0.08, 0.3, 0.02), "satin", 0x3b404d, { p: [0, top - 0.02, 0] });
    kit.add(G.chamfer(0.16, 0.3, 0.12, 0.02), "plastic", 0xffc21a, { p: [0, 1.6, -0.2] });
    const root = new THREE.Group();
    root.name = "trafficPole";
    root.add(kit.build(), blobShadow(1.8, 1.8));
    return root;
  });
}

/** `buildBush()` → a trimmed hedge in a concrete planter (drive through it: it is a trigger). */
export function buildBush(): THREE.Group {
  return instance("bush", () => {
    const kit = new Kit();
    kit.add(G.chamfer(2.36, 0.44, 2.36, 0.06), "matte", 0xcfc6b8, { p: [0, 0.22, 0] });
    kit.add(G.box(2.1, 0.02, 2.1), "matte", 0x5a4232, { p: [0, 0.44, 0] });
    const blob = (seed: number) => once(`g:leafblob:${seed}`, () => {
      const g = mergeVertices(new THREE.IcosahedronGeometry(1, 1).deleteAttribute("normal").deleteAttribute("uv"));
      const p = g.getAttribute("position");
      const v = new THREE.Vector3();
      for (let i = 0; i < p.count; i++) {
        v.fromBufferAttribute(p, i);
        const k = 1 + 0.12 * Math.sin(v.x * 5.3 + seed) * Math.cos(v.y * 4.1 - seed) + 0.06 * Math.sin(v.z * 7.7 + seed * 2);
        p.setXYZ(i, v.x * k, v.y * k, v.z * k);
      }
      g.computeVertexNormals();
      return g;
    });
    const greens = [0x3fae4a, 0x2f9a43, 0x56c25a, 0x48b552];
    const puffs: [number, number, number, number][] = [
      [0, 0.82, 0, 0.62], [-0.62, 0.72, -0.5, 0.5], [0.6, 0.74, -0.52, 0.52], [-0.6, 0.72, 0.52, 0.52], [0.62, 0.7, 0.55, 0.5],
      [0, 0.7, -0.68, 0.5], [0, 0.7, 0.68, 0.5], [-0.72, 0.66, 0, 0.46], [0.72, 0.66, 0, 0.46]
    ];
    puffs.forEach(([x, y, z, r], i) => kit.add(blob(i % 3), "plastic", greens[i % greens.length], { p: [x, y, z], s: [r * 1.05, r * 0.9, r * 1.05] }));
    const flowers = [[0.3, 1.3, -0.35], [-0.45, 1.12, -0.6], [0.7, 1.05, 0.2], [-0.2, 1.33, 0.3], [-0.85, 0.95, 0.1], [0.45, 1.0, -0.85], [0.1, 1.05, 0.9]];
    flowers.forEach(([x, y, z], i) => kit.add(G.sphere(0.07, 6, 4), "paint", i % 2 ? 0xff7ab8 : 0xfff3a8, { p: [x, y, z] }));
    const root = new THREE.Group();
    root.name = "bush";
    root.add(kit.build(), blobShadow(3.0, 3.0));
    return root;
  });
}

/** `buildPillar()` → a concrete pillar between lanes (6 m) with a hazard wrap at its foot. */
export function buildPillar(): THREE.Group {
  return instance("pillar", () => {
    const kit = new Kit();
    const h = PILLAR.height;
    kit.add(G.chamfer(1.3, 0.3, 1.3, 0.05), "matte", 0xa9adb8, { p: [0, 0.15, 0] });
    kit.add(G.chamfer(1.08, h - 0.6, 1.08, 0.08), "matte", 0xd3d6de, { p: [0, 0.3 + (h - 0.6) / 2, 0] });
    kit.add(G.chamfer(1.3, 0.32, 1.3, 0.05), "matte", 0xa9adb8, { p: [0, h - 0.16, 0] });
    for (const y of [2.6, 4.4]) kit.add(G.box(1.1, 0.04, 1.1), "matte", 0xb9bcc6, { p: [0, y, 0] });
    const wrap = printMaterial("pillar-wrap", stripesTex("#ffc21a", "#17181f", 3, 256, 256), { roughness: 0.5 });
    const faces: [[number, number, number], [number, number, number]][] = [[[0, 0.95, -0.545], FACE.front], [[0, 0.95, 0.545], FACE.back], [[0.545, 0.95, 0], FACE.left], [[-0.545, 0.95, 0], FACE.right]];
    for (const [p, r] of faces) kit.add(G.plane(1.0, 1.2), wrap, 0xffffff, { p, r });
    for (const [p, r] of faces) kit.add(G.plane(0.12, 1.6), "glow", hdr(0xff3348, 0.9), { p: [p[0] * 1.002, 3.4, p[2] * 1.002], r });
    const root = new THREE.Group();
    root.name = "pillar";
    root.add(kit.build(), blobShadow(2.2, 2.2));
    return root;
  });
}
